import React, { useState, useRef } from 'react';
import '../App.css';

interface ProfilePhotoUploadProps {
  onComplete: () => void;
  onSkip: () => void;
}

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  onComplete,
  onSkip,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPG, PNG, GIF, or WEBP');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size exceeds 5MB limit');
      return;
    }

    setSelectedFile(file);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a photo');
      return;
    }

    // Check for authentication token
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required. Please log in again.');
      // Redirect to login after a delay
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('photo', selectedFile);

      const apiBaseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

      const response = await fetch(`${apiBaseURL}/api/v1/internal/users/upload-profile-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        onComplete();
      } else {
        setError(data.error || 'Failed to upload photo');
      }
    } catch (err) {
      setError('Failed to upload photo. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="profile-photo-container">
      <div className="profile-photo-card">
        <div className="profile-photo-header">
          <h2>Let's Add Your Photo!</h2>
          <p className="profile-photo-subtitle">
            Help others recognize you by adding a friendly photo
          </p>
        </div>

        <div className="photo-upload-area">
          {preview ? (
            <div className="photo-preview">
              <img src={preview} alt="Profile preview" className="preview-image" />
              <button
                onClick={() => {
                  setSelectedFile(null);
                  setPreview('');
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                className="btn-change-photo"
                disabled={uploading}
              >
                Change Photo
              </button>
            </div>
          ) : (
            <div
              className="photo-drop-zone"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="upload-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <p className="upload-text">Click to upload or drag and drop</p>
              <p className="upload-hint">JPG, PNG, GIF or WEBP (max 5MB)</p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className="file-input-hidden"
          />
        </div>

        {error && <div className="upload-error">{error}</div>}

        <div className="photo-upload-actions">
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="btn-upload-photo"
          >
            {uploading ? 'Uploading...' : 'Upload Photo'}
          </button>

          <button
            onClick={onSkip}
            disabled={uploading}
            className="btn-skip-photo"
          >
            Skip for Now
          </button>
        </div>

        <div className="photo-upload-help">
          <p>This step is optional. You can add a photo later in your profile settings.</p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePhotoUpload;
